import { Link, useNavigate } from 'react-router-dom';
import { useProjects } from '../../../api/projects';
import { useDonations } from '../../../api/donations';
import { ProjectStatus, DonationStatus } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { EmptyState } from '../shared/EmptyState';
import { OrgIcon } from '../shared/EntityIcons';

export function DonorDashboard() {
  const navigate = useNavigate();
  const { data: activeProjects } = useProjects({ status: [ProjectStatus.ACTIVE], limit: 5 });
  const { data: myDonations } = useDonations({ limit: 50 });

  const pledged = myDonations?.data.filter((t) => t.status === DonationStatus.PLEDGED) ?? [];
  const shipped = myDonations?.data.filter((t) => t.status === DonationStatus.SHIPPED) ?? [];
  const delivered = myDonations?.data.filter((t) => t.status === DonationStatus.DELIVERED) ?? [];
  const received = myDonations?.data.filter((t) => t.status === DonationStatus.RECEIVED) ?? [];
  const actionCount = pledged.length + shipped.length + delivered.length + received.length;

  return (
    <div className="space-y-8">
      {/* Action Required */}
      {actionCount > 0 ? (
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-amber-800">Action Required</h3>
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">{actionCount}</span>
          </div>

          {pledged.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Pledged &mdash; Ready to Ship ({pledged.length})</p>
              <div className="space-y-2">
                {pledged.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">{donation.askCrop} &middot; {donation.quantityKg > 0 ? `${donation.quantityKg}kg` : ''}{donation.quantityPackets > 0 ? ` ${donation.quantityPackets} pkts` : ''}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Mark as Shipped &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {shipped.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Shipped &mdash; Awaiting Delivery ({shipped.length})</p>
              <div className="space-y-2">
                {shipped.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">{donation.askCrop} &middot; {donation.recipientOrganization}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Mark as Delivered &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {delivered.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Delivered &mdash; Awaiting Receipt ({delivered.length})</p>
              <div className="space-y-2">
                {delivered.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">{donation.askCrop} &middot; ${donation.fairMarketValueUsd.toLocaleString()}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Awaiting Recipient &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {received.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Received &mdash; Ready to Verify ({received.length})</p>
              <div className="space-y-2">
                {received.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">{donation.askCrop} &middot; ${donation.fairMarketValueUsd.toLocaleString()}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Verify Donation &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : myDonations ? (
        <div className="rounded-lg border-l-4 border-spi-green bg-green-50 p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-spi-green flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800">All caught up! No donations need your attention right now.</p>
        </div>
      ) : null}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Active Pledges</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">
            {myDonations?.data.filter((t) => t.status !== DonationStatus.CANCELLED && t.status !== DonationStatus.VERIFIED).length ?? 0}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Completed Donations</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">
            {myDonations?.data.filter((t) => t.status === DonationStatus.VERIFIED).length ?? 0}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Active Projects</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{activeProjects?.total ?? 0}</p>
        </div>
      </div>

      {/* Active Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl">Active Projects</h3>
          <Link to="/marketplace/projects" className="text-sm text-spi-green font-semibold hover:underline">
            View all
          </Link>
        </div>
        {activeProjects?.data.length ? (
          <div className="space-y-3">
            {activeProjects.data.map((project) => (
              <Link key={project.id} to={`/marketplace/projects/${project.id}`} className="card p-4 flex items-center justify-between hover:shadow-lg">
                <div>
                  <h4 className="font-semibold text-spi-charcoal text-base">{project.title}</h4>
                  <p className="text-sm text-spi-slate">
                    <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/marketplace/organizations/${project.orgId}`); }}
                      className="inline-flex items-center gap-1 hover:text-spi-green cursor-pointer"
                    >
                      <OrgIcon className="w-3.5 h-3.5" />{project.orgName}
                    </span>
                    {' '}&middot; {project.country} &middot; {project.openAskCount} open asks
                  </p>
                </div>
                <StatusBadge status={project.status} />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title="No active projects" description="Check back soon for new seed requests." />
        )}
      </div>

      {/* My Transactions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl">My Recent Donations</h3>
          <Link to="/marketplace/donations" className="text-sm text-spi-green font-semibold hover:underline">
            View all
          </Link>
        </div>
        {myDonations?.data.length ? (
          <div className="space-y-3">
            {myDonations.data.map((donation) => (
              <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="card p-4 flex items-center justify-between hover:shadow-lg">
                <div>
                  <h4 className="font-semibold text-spi-charcoal text-base">{donation.projectTitle}</h4>
                  <p className="text-sm text-spi-slate">{donation.askCrop} &middot; {donation.quantityKg > 0 ? `${donation.quantityKg}kg` : ''}{donation.quantityPackets > 0 ? ` ${donation.quantityPackets} pkts` : ''} &middot; ${donation.fairMarketValueUsd.toLocaleString()}</p>
                </div>
                <StatusBadge status={donation.status} />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title="No donations yet" description="Browse active projects to make your first seed donation." />
        )}
      </div>
    </div>
  );
}
